import type { ReactElement } from 'react';
import React from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { SelectField } from 'atom-components/form-fields-for-formik/select-field/select-field';
import type { ClusterCustomerWithCustomerGroup } from 'services/assist-api/entities/customer-groups-customers/customer-groups-customers';
import { useClusterCustomers } from 'services/assist-api/entities/customer-groups-customers/customer-groups-customers';
import { useAuthCustomer } from 'services/providers/auth.hooks';
import { isInitialLoad } from 'utils/check-query-status';

/**
 * Select a cluster that the current customer belongs to
 */
export default function ClusterField(): ReactElement {
  const customerId = useAuthCustomer()?.id;
  /**
   * Request clusters from the API
   * - goes through customer-groups-customers as that has better access controls and filters
   */
  const clusterCustomers: UseQueryResult<RequestResponse<ClusterCustomerWithCustomerGroup[]>> =
    useClusterCustomers<ClusterCustomerWithCustomerGroup>(
      {
        filters: { customer: customerId },
        include: 'customerGroup',
        groups: ['customergroupscustomers-customergroup', 'customergroup-id', 'customergroup-name']
      },
      { enabled: !!customerId }
    );

  return (
    <SelectField
      name="cluster"
      label="Cluster"
      options={clusterCustomers.data?.json || []}
      getOptionLabel={(o): string => o.customerGroup.name}
      getOptionValue={(o): string => String(o.customerGroup.id)}
      getValue={(o: { customerGroup: { id: string } }): string => o.customerGroup.id}
      isLoading={isInitialLoad(clusterCustomers)}
      hideSuccessValidation
      isOnlyValue
      isRequired
    />
  );
}

export const initialCluster = { cluster: undefined };
